import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import { ArrowRight, Users, Award, Globe, Shield } from "lucide-react"

export function AboutPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden container py-24 sm:py-32">
          {/* Background pattern */}
          <div className="absolute inset-0 -z-10 bg-grid-black/5 dark:bg-grid-white/5">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"></div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto flex max-w-[64rem] flex-col items-center text-center gap-4"
          >
            <div className="inline-flex items-center rounded-2xl glass-effect px-6 py-2 text-sm font-medium text-primary mb-4">
              <span className="block h-2 w-2 rounded-full bg-primary mr-2 animate-pulse" />
              Serving properties nationwide
            </div>
            
            <h1 className="font-heading text-4xl font-bold sm:text-5xl md:text-6xl lg:text-7xl">
              About <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">FlexHelp</span>
            </h1>
            <p className="max-w-[42rem] leading-normal text-muted-foreground sm:text-xl sm:leading-8">
              We're revolutionizing property maintenance with technology and trust. Our mission is to make property maintenance simple, efficient, and reliable.
            </p>
          </motion.div>
        </section>

        {/* Stats Section */}
        <section className="container py-12">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card variant="glass" className="p-6 flex flex-col items-center justify-center h-full">
                <div className="text-3xl font-bold md:text-4xl text-primary">500+</div>
                <div className="mt-1 text-sm text-muted-foreground md:text-base">Properties Served</div>
              </Card>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card variant="glass" className="p-6 flex flex-col items-center justify-center h-full">
                <div className="text-3xl font-bold md:text-4xl text-primary">50k+</div>
                <div className="mt-1 text-sm text-muted-foreground md:text-base">Tasks Completed</div>
              </Card>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Card variant="glass" className="p-6 flex flex-col items-center justify-center h-full">
                <div className="text-3xl font-bold md:text-4xl text-primary">97%</div>
                <div className="mt-1 text-sm text-muted-foreground md:text-base">Customer Satisfaction</div>
              </Card>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Card variant="glass" className="p-6 flex flex-col items-center justify-center h-full">
                <div className="text-3xl font-bold md:text-4xl text-primary">20+</div>
                <div className="mt-1 text-sm text-muted-foreground md:text-base">US States</div>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="container py-24 sm:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto flex max-w-[58rem] flex-col items-center text-center"
          >
            <h2 className="font-heading text-3xl font-bold leading-[1.1] sm:text-4xl md:text-5xl">
              Our Core Values
            </h2>
            <p className="mt-4 text-muted-foreground sm:text-lg">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="mx-auto mt-16 grid justify-center gap-8 sm:grid-cols-2 md:max-w-[64rem] lg:grid-cols-4">
            {[
              {
                icon: Users,
                title: "Customer First",
                description: "We prioritize our customers' needs above all else"
              },
              {
                icon: Award,
                title: "Excellence",
                description: "We strive for excellence in every service we provide"
              },
              {
                icon: Globe,
                title: "Sustainability",
                description: "We're committed to environmentally responsible practices"
              },
              {
                icon: Shield,
                title: "Trust",
                description: "We build lasting relationships based on trust and reliability"
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative overflow-hidden rounded-lg border bg-background p-2"
              >
                <div className="flex h-[180px] flex-col justify-between rounded-md p-6">
                  <value.icon className="h-12 w-12 text-primary" />
                  <div className="space-y-2">
                    <h3 className="font-bold">{value.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {value.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="border-t">
          <div className="container py-24 sm:py-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto flex max-w-[58rem] flex-col items-center justify-center gap-4 text-center"
            >
              <h2 className="font-heading text-3xl font-bold leading-[1.1] sm:text-4xl md:text-5xl">
                Ready to experience better property maintenance?
              </h2>
              <p className="max-w-[85%] leading-normal text-muted-foreground sm:text-lg sm:leading-7">
                Join thousands of satisfied property managers who trust FlexHelp
              </p>
              <Link to="/register">
                <Button size="lg" className="mt-4">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
    </div>
  )
}